<mat-toolbar color="primary" class="action-toolbar">
    <h3>Inbox</h3>
</mat-toolbar>

<div class="patient-list-container">

    <div class="filter-wrapper">
        <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Search</mat-label>
            <input matInput [(ngModel)]="filterValue" (keyup.enter)="applyFilter()"
                placeholder="Search the inbox" data-test="inbox-search" #input>
            <mat-icon matSuffix *ngIf="!filterValue" class="clickable" (click)="applyFilter()">search</mat-icon>
            <mat-icon matSuffix *ngIf="filterValue" (click)="clearFilter()" class="clickable">close</mat-icon>
            <!--
                The hint is not decoration. Patient names are encrypted with no blind index, so the
                server matches the document name and patient number across the practice and the
                name half is applied to the loaded page only. A search box that silently misses
                matches is worse than one that states its scope.
            -->
            <mat-hint>{{ searchHint() }}</mat-hint>
        </mat-form-field>
        <button mat-icon-button class="refresh-button" (click)="refresh()" matTooltip="Refresh"
            data-test="inbox-refresh">
            <mat-icon>refresh</mat-icon>
        </button>
    </div>

    <!--
        ONE row template for every tab and every source.

        The four tabs differ only in which rows they hold and which action a row offers, so a
        second copy of this markup per tab would be four places to fix a spacing bug. It is also
        what keeps a future source cheap: an incoming referral draws through this, with its own
        icon out of INBOX_ITEM_TYPES and its own stateLabel off the server.

        NOTHING HERE BRANCHES ON `item.state`. The server decides the words; the client draws them.
        A `*ngIf` chain over a source's own vocabulary would be a blank row the day a source ships
        a state this build has not heard of.
    -->
    <ng-template #itemRow let-item>
        <mat-list-item class="auto-height inbox-row" (click)="openItem(item)"
            [class.flagged]="isFlagged(item)" [attr.data-test-item]="keyOf(item)">
            <div class="inbox-item">
                <!--
                    THE LIGATURE NAME MUST TOUCH THE TAGS. Do not reformat this onto three lines.

                    `mat-icon` is a 24px box with `overflow: hidden`, and the Material Icons glyph
                    is itself 24px wide. Whitespace around the interpolation is a line-break
                    opportunity, and a 24px glyph cannot share a 24px line with a space — so it
                    wraps to a SECOND line, 24px down, outside the box. The icon renders perfectly
                    and is clipped out of sight, which is why this looked like "no icon at all"
                    rather than like a broken glyph. Measured in the browser: the padded form
                    produces five line boxes with the glyph at dy 22.9; the trimmed form, one at
                    dy -1.1.
                -->
                <mat-icon class="type-icon" [ngClass]="typeOf(item).cssClass"
                    [matTooltip]="typeOf(item).label"
                    [attr.aria-label]="typeOf(item).label">{{ typeOf(item).icon }}</mat-icon>

                <div class="item-info">
                    <div class="item-header">
                        <span class="patient-name-text" *ngIf="item.patientName">
                            <b>{{ item.patientName }}</b>
                            <span class="patient-number" *ngIf="item.patientNumber">
                                ({{ item.patientNumber }})
                            </span>
                        </span>
                        <span class="state-chip" *ngIf="item.stateLabel">{{ item.stateLabel }}</span>
                        <mat-icon class="flag-icon" *ngIf="isFlagged(item)"
                            [matTooltip]="item.flagNote || 'Flagged'">flag</mat-icon>
                    </div>

                    <div class="item-title">{{ item.title }}</div>

                    <!--
                        ONE LINE OF FACTS, pipe-separated.

                        The category, the time, and who has already acted were four stacked lines,
                        which made every row four lines tall and buried the ones that matter (who
                        signed it off) below the one that rarely does (the category).

                        The separators are drawn in CSS by `* + *::before`, NOT written here. With
                        four `*ngIf`s, hard-coded pipes would mean a leading or doubled pipe
                        whenever the first fact is absent — a row with no category, or none of the
                        review names, is the common case, not the edge one.
                    -->
                    <div class="item-facts">
                        <span class="item-details" *ngIf="item.subtitle">{{ item.subtitle }}</span>
                        <span class="item-meta"
                            *ngIf="item.occurredAt">{{ item.occurredAt | date: 'yyyy-MM-dd HH:mm' }}</span>
                        <span class="item-meta"
                            *ngIf="item.reviewedByName">Signed off by {{ item.reviewedByName }}</span>
                        <span class="item-meta"
                            *ngIf="item.validatedByName">Validated by {{ item.validatedByName }}</span>
                    </div>
                    <!--
                        Only ever drawn on the Flagged tab in practice, but not conditioned on the
                        tab: a flag note is worth seeing wherever the row appears, and the row is
                        the same row.
                    -->
                    <div class="item-meta flag-note" *ngIf="item.flagNote">
                        <mat-icon>sticky_note_2</mat-icon>
                        {{ item.flagNote }}<span *ngIf="item.flaggedByName"> — {{ item.flaggedByName }}</span>
                    </div>
                </div>

                <div class="item-actions">
                    <button mat-stroked-button color="primary" class="review-button"
                        *ngIf="actionFor(item) as action" (click)="openItem(item, $event)"
                        [attr.data-test-action]="action.key">
                        {{ action.label }}
                    </button>
                    <button mat-icon-button class="flag-button" (click)="toggleFlag($event, item)"
                        *ngIf="item.canFlag !== false" [disabled]="isBusy(item)"
                        [matTooltip]="isFlagged(item) ? 'Clear flag' : 'Flag for attention'">
                        <mat-icon>{{ isFlagged(item) ? 'flag' : 'outlined_flag' }}</mat-icon>
                    </button>
                    <button mat-icon-button class="file-button" (click)="openPatientFile($event, item)"
                        matTooltip="Open patient file" *ngIf="item.patientId">
                        <mat-icon>visibility</mat-icon>
                    </button>
                </div>
            </div>
        </mat-list-item>
    </ng-template>

    <!--
        ONE body template for every tab, taking the tab name as its context.

        `groups[tab]` is already date-grouped and ordered by the component; the server ordered the
        rows within each group and this deliberately does not re-sort them, or the drawn order
        would fight the keyset cursor and paging would look non-deterministic.
    -->
    <ng-template #tabBody let-tab>
        <div class="tab-content">
            <ng-container *ngIf="groups[tab].length; else emptyTab">
                <mat-list>
                    <ng-container *ngFor="let group of groups[tab]; trackBy: trackGroup">
                        <div class="list-header">{{ group.date | date: 'longDate' }}</div>
                        <ng-container *ngFor="let item of group.items; let last = last; trackBy: trackItem">
                            <ng-container *ngTemplateOutlet="itemRow; context: { $implicit: item }"></ng-container>
                            <mat-divider *ngIf="!last"></mat-divider>
                        </ng-container>
                    </ng-container>

                    <div class="load-more" *ngIf="state[tab].hasMore">
                        <button mat-stroked-button (click)="loadMore()" [disabled]="state[tab].loading"
                            data-test="inbox-load-more">
                            {{ state[tab].loading ? 'Loading…' : 'Load more' }}
                        </button>
                    </div>
                    <div class="mobile-spacer"></div>
                </mat-list>
            </ng-container>

            <!--
                THREE empty states, not one, because they mean different things to a practice:
                still loading, nothing matched the search, or genuinely nothing to do. Collapsing
                them sends somebody to look for a document that is simply filtered out.
            -->
            <ng-template #emptyTab>
                <p class="no-records-message" *ngIf="state[tab].loading">Loading…</p>
                <p class="no-records-message" *ngIf="!state[tab].loading && filterValue">
                    Nothing in {{ labelFor(tab) }} matches “{{ filterValue }}”.
                </p>
                <p class="no-records-message" *ngIf="!state[tab].loading && !filterValue">
                    {{ tab === 'new' ? 'Nothing new has come in.'
                     : tab === 'for-review' ? 'Nothing is waiting to be validated.'
                     : tab === 'flagged' ? 'Nothing is flagged.'
                     : 'Nothing is outstanding.' }}
                </p>
            </ng-template>
        </div>
    </ng-template>

    <mat-tab-group (selectedIndexChange)="onTabChange($event)" [selectedIndex]="selectedTabIndex"
        class="tab-group" matTabGroupGesture [swipeLimitWidth]="80" [connectEdges]="true">
        <mat-tab *ngFor="let tab of tabs">
            <ng-template mat-tab-label>
                {{ tabLabels[tab] }} ({{ countFor(tab) }})
            </ng-template>
            <!--
                `matTabContent` is LAZY, deliberately: without it Angular builds all four tab
                bodies on first paint, and this page loads a tab's rows only when it is opened.
                Four eagerly-built empty bodies is four `*ngFor`s over nothing on every change
                detection pass, for three tabs nobody is looking at.
            -->
            <ng-template matTabContent>
                <ng-container *ngTemplateOutlet="tabBody; context: { $implicit: tab }"></ng-container>
            </ng-template>
        </mat-tab>
    </mat-tab-group>
</div>
